import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import { Box, Card, IconButton, Typography } from '@mui/material';
import { DataGrid, GridCellParams, GridRow } from '@mui/x-data-grid';
import { GridRowProps } from '@mui/x-data-grid/components/GridRow';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';

import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as tripPackageInventorySlice from '../../../data/tripPackageInventory.slice';
import * as providerSlice from '../../../data/provider.slice';
import TripPackageDialog from './TripPackageDialog';
import { useLoggedInProviderId } from '../../../hooks/useLoggedInProviderId';
import { DateTime } from 'luxon';
import { TripLegPackage } from '../../../data/models/trip-leg-package.model';
import { selectTripLegStatuses } from '../../../data/reference.slice';
import * as referenceSlice from '../../../data/reference.slice';

const PREFIX = 'TempNotifications';

const classes = {
  container: `${PREFIX}-container`,
  grid: `${PREFIX}-grid`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    margin: theme.spacing(2),
  },

  [`& .${classes.grid}`]: {
    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus':
      {
        outline: 'none',
      },
  },
}));

function RowWithFSTagging(props: GridRowProps) {
  return <GridRow {...props} data-fs-element="OpenTripPackageButton" />;
}

export default function TempNotifications() {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();

  const [providerId, setProviderId] = useState<number>();
  const tripLegStatuses = useAppSelector(selectTripLegStatuses);

  /***** start temporary code to allow PR users to view Benson page *****/
  const loggedInProviderId = useLoggedInProviderId();
  const bensonProviderId = useAppSelector(providerSlice.selectBensonProviderID);

  useEffect(() => {
    if (!loggedInProviderId) {
      dispatch(providerSlice.fetchBensonProviderIDAsync());
    }
  }, [loggedInProviderId]);

  useEffect(() => {
    if (loggedInProviderId) {
      setProviderId(loggedInProviderId);
    } else if (bensonProviderId) {
      setProviderId(bensonProviderId);
    }
  }, [loggedInProviderId, bensonProviderId]);
  /****** end temporary code *****/

  React.useEffect(() => {
    dispatch(referenceSlice.fetchTripLegStatusesAsync());
  }, []);

  const [tripPackageDialogOpen, setTripPackageDialogOpen] =
    React.useState<boolean>(false);
  const [packageUUID, setPackageUUID] = React.useState<string>('');

  const loading = useAppSelector(
    tripPackageInventorySlice.selectLoadingOfferedPackages,
  );
  const offeredPackages = useAppSelector(
    tripPackageInventorySlice.selectOfferedPackages,
  );

  React.useEffect(() => {
    if (providerId && !!tripLegStatuses?.length) {
      dispatch(
        tripPackageInventorySlice.getOfferedPackagesAsync({
          transportationProviderID: providerId,
          pageSize: 1000,
        }),
      );
    }
  }, [providerId, tripLegStatuses]);

  const renderDateTime = (params: GridCellParams<any, TripLegPackage>) => {
    const d = DateTime.fromISO(params.row.createdOnUTC);
    return d.toFormat('M/d/yyyy h:mm A');
  };

  const renderMessage = (params: GridCellParams<any, TripLegPackage>) => {
    return `Trip leg package offered: ${params.row.tripLegs.length} trips`;
  };

  const handleTripPackageClicked = (pkg: TripLegPackage) => {
    setPackageUUID('' + pkg.tripLegPackageUUID);
    setTripPackageDialogOpen(true);
  };

  const getActionComponent = (pkg: TripLegPackage): React.ReactNode => {
    return (
      <>
        <IconButton
          data-testid="accept"
          onClick={() => {
            handleTripPackageClicked(pkg);
          }}
          size="medium"
          color="primary">
          <SettingsOutlinedIcon />
        </IconButton>
      </>
    );
  };

  const columns = [
    {
      field: 'time',
      headerName: t('notification.dateTime'),
      width: 150,
      sortable: false,
      renderCell: renderDateTime,
    },
    {
      field: 'message',
      headerName: t('notification.description'),
      flex: 1,
      sortable: false,
      renderCell: renderMessage,
    },
    {
      field: 'actions',
      headerName: ' ',
      width: 100,
      sortable: false,
      renderCell: (params: GridCellParams<any, TripLegPackage>) =>
        getActionComponent(params.row),
    },
  ];

  const closeTripPackageDialog = () => {
    setTripPackageDialogOpen(false);
  };

  return (
    <StyledBox className={classes.container}>
      <Box sx={{ marginBottom: 2 }}>
        <Typography variant="h4">{t('common.tripPackages')}</Typography>
      </Box>
      <Card>
        <DataGrid
          getRowId={(row) => row.tripLegPackageUUID}
          onRowClick={(params) => {
            handleTripPackageClicked(params.row as TripLegPackage);
          }}
          rows={offeredPackages}
          columns={columns}
          autoHeight={true}
          loading={loading}
          hideFooter={true}
          className={classes.grid}
          checkboxSelection={false}
          disableColumnMenu={true}
          disableColumnSelector={true}
          components={{ Row: RowWithFSTagging }}
        />
      </Card>
      <TripPackageDialog
        open={tripPackageDialogOpen}
        packageUUID={packageUUID}
        close={closeTripPackageDialog}
      />
    </StyledBox>
  );
}
